<template>
    <main class="font-sans text-gray-800 antialiased bg-white selection:bg-orange-100 selection:text-orange-700">
        
        <landing-header />
        
        <section class="pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-gradient-to-b from-orange-50/50 via-white to-white">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    
                    <div class="z-10">
                        <div class="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-orange-100 mb-8 hover:shadow-md transition-shadow">
                            <span class="flex h-2 w-2 rounded-full bg-orange-500 animate-ping"></span>
                            <span class="text-xs font-black uppercase tracking-widest text-gray-600">
                                Le logiciel métier #1 pour le CVC
                            </span>
                        </div>
                        
                        <h1 class="text-5xl lg:text-7xl font-black text-gray-900 leading-[1.05] mb-8">
                            Vous êtes chauffagiste.<br />
                            <span class="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">Pas secrétaire.</span>
                        </h1>
                        
                        <p class="text-xl text-gray-600 leading-relaxed mb-10 max-w-xl">
                            Le seul logiciel de <strong>gestion des interventions</strong> qui libère les professionnels du chauffage et de la climatisation du poids administratif. Sécurisez votre conformité F-Gaz et pilotez vos techniciens.
                        </p>
                        
                        <div class="flex flex-col sm:flex-row gap-5">
                            <router-link to="/contact" class="bg-orange-600 text-white px-10 py-5 rounded-2xl font-black text-xl shadow-xl shadow-orange-500/30 hover:bg-orange-700 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3">
                                Demander une démo <i class="fa-solid fa-chevron-right text-sm"></i>
                            </router-link>
                            
                            <router-link to="/tarifs" class="bg-white border-2 border-gray-200 text-gray-900 px-10 py-5 rounded-2xl font-black text-xl hover:border-orange-500 hover:text-orange-600 transition-all flex items-center justify-center">
                                Voir les tarifs
                            </router-link>
                        </div>
                        
                        <div class="mt-8 flex items-center gap-4 text-sm font-bold text-gray-500 uppercase tracking-widest">
                            <span><i class="fa-solid fa-check text-orange-500 mr-1"></i> Sans engagement</span>
                            <span><i class="fa-solid fa-check text-orange-500 mr-1"></i> Configuré en 24h</span>
                        </div>
                    </div>
                    
                    <div class="relative flex items-center justify-center">
                        <div class="absolute inset-0 bg-gradient-to-tr from-orange-400/20 to-red-400/20 rounded-full blur-3xl scale-90 animate-pulse"></div>
                        
                        <div @click="openVideo" class="relative z-10 w-full aspect-[7/6] rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/50 cursor-pointer group transition-all duration-700 ease-out" :class="{ 'scale-95 opacity-0': videoAnimating }" >
                            
                            <video class="w-full h-full object-cover transition duration-700 group-hover:scale-105" autoplay muted loop playsinline >
                                <source src="https://storage.termoflow.fr/file/xH03wk5nBk" type="video/mp4" />
                            </video>
                            
                            <div class="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent"></div>
                            
                            <div class="absolute inset-0 flex items-center justify-center">
                                <div class="w-24 h-24 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center shadow-2xl transition duration-300 group-hover:scale-110 group-hover:shadow-orange-500/50 animate-bounce-slow">
                                    <i class="fa-solid fa-play text-orange-600 text-3xl ml-1"></i>
                                </div>
                            </div>
                            
                            <div class="absolute bottom-8 left-8 text-white">
                                <p class="text-sm font-bold uppercase tracking-widest text-orange-400 mb-1">Notre objectif :</p>
                                <p class="text-2xl font-black">Simplifier votre administratif</p>
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </section>

        <section class="py-24 bg-white">
            <div class="max-w-7xl mx-auto px-4">
                <div class="text-center mb-16">
                    <h2 class="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                        L'administratif étouffe <span class="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">votre croissance</span>
                    </h2>
                    <p class="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        Entre la gestion des interventions terrain, la complexité des obligations F-Gaz et le suivi client, les entreprises de chauffage et clim perdent jusqu'à 15h par semaine en saisie manuelle.
                    </p>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div class="p-10 bg-gray-50 rounded-[2rem] border border-gray-100 hover:shadow-lg transition-all">
                        <div class="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-6 text-2xl text-orange-600"><i class="fa-solid fa-file-invoice"></i></div>
                        <h3 class="text-xl font-black mb-3">Surcharge paperasse</h3>
                        <p class="text-gray-600 leading-relaxed">
                            CERFA de manipulation de fluides, registres, rapports d'intervention… La charge administrative tue la rentabilité de vos chantiers.
                        </p>
                    </div>
                    <div class="p-10 bg-gray-50 rounded-[2rem] border border-gray-100 hover:shadow-lg transition-all">
                        <div class="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-6 text-2xl text-orange-600"><i class="fa-solid fa-network-wired"></i></div>
                        <h3 class="text-xl font-black mb-3">Outils dispersés</h3>
                        <p class="text-gray-600 leading-relaxed">
                            Un agenda partagé, Excel pour les stocks de bouteilles, du papier pour les rapports... L'information se perd entre le bureau et le camion.
                        </p>
                    </div>
                    <div class="p-10 bg-gray-50 rounded-[2rem] border border-gray-100 hover:shadow-lg transition-all">
                        <div class="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-6 text-2xl text-orange-600"><i class="fa-solid fa-chart-area"></i></div>
                        <h3 class="text-xl font-black mb-3">Pilotage à l'aveugle</h3>
                        <p class="text-gray-600 leading-relaxed">
                            Sans centralisation, impossible de savoir avec précision la rentabilité de vos contrats, la charge de travail de vos techniciens ou les urgences à traiter. Vous pilotez à l'intuition.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-24 bg-orange-50/50 overflow-hidden">
            <div class="max-w-7xl mx-auto px-4">
                <div class="flex flex-col lg:flex-row items-center gap-16">
                    <div class="lg:w-1/2">
                        <span class="text-orange-600 font-bold tracking-widest uppercase text-sm mb-2 block">Opérations & Planning</span>
                        <h2 class="text-4xl font-black text-gray-900 mb-6">Planifiez et suivez vos interventions <br>en temps réel.</h2>
                        <p class="text-lg text-gray-600 mb-8 leading-relaxed">
                            Centralisez la gestion de vos interventions chauffagistes. Termoflow affecte les urgences intelligemment et vous donne une vision claire de l'activité de vos techniciens.
                        </p>
                        <ul class="space-y-4 mb-10">
                            <li class="flex items-center gap-3 text-gray-800 font-bold"><i class="fa-solid fa-circle-check text-orange-500"></i> Historique complet par appareil (chaudière, PAC, etc.)</li>
                            <li class="flex items-center gap-3 text-gray-800 font-bold"><i class="fa-solid fa-circle-check text-orange-500"></i> Gestion des interventions planifiées</li>
                            <li class="flex items-center gap-3 text-gray-800 font-bold"><i class="fa-solid fa-circle-check text-orange-500"></i> Affectation rapide des interventions aux techniciens</li>
                        </ul>
                        <router-link to="/planification" class="inline-flex items-center gap-2 text-white bg-gray-900 px-8 py-4 rounded-xl font-bold hover:bg-orange-600 transition-colors">
                            Découvrir la planification <i class="fa-solid fa-arrow-right"></i>
                        </router-link>
                    </div>
                    <div class="lg:w-1/2 relative">
                        <div class="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-400 blur-3xl opacity-20 rounded-full"></div>
                        <img src="https://storage.termoflow.fr/file/tyi9DuY5ZS" alt="Interface de gestion des interventions chauffagiste" class="relative z-10 w-full rounded-2xl shadow-2xl border border-gray-100 transform hover:-translate-y-2 transition-transform duration-500">
                    </div>
                </div>
            </div>
        </section>

        <section class="py-24 bg-white overflow-hidden">
            <div class="max-w-7xl mx-auto px-4">
                <div class="flex flex-col lg:flex-row-reverse items-center gap-16">
                    <div class="lg:w-1/2">
                        <span class="text-orange-600 font-bold tracking-widest uppercase text-sm mb-2 block">Conformité F-Gaz</span>
                        <h2 class="text-4xl font-black text-gray-900 mb-6">Vos CERFA et registres de fluides générés en 1 clic.</h2>
                        <p class="text-lg text-gray-600 mb-8 leading-relaxed">
                            La réglementation sur les fluides frigorigènes est stricte. Termoflow pré-remplit vos documents officiels, suit vos bouteilles de gaz à la trace et alerte sur les étalonnages de détecteurs.
                        </p>
                        <ul class="space-y-4 mb-10">
                            <li class="flex items-center gap-3 text-gray-800 font-bold"><i class="fa-solid fa-circle-check text-orange-500"></i> CERFA pré-remplis à 90% pour éviter les saisies</li>
                            <li class="flex items-center gap-3 text-gray-800 font-bold"><i class="fa-solid fa-circle-check text-orange-500"></i> Registre de mouvements de fluides automatique</li>
                            <li class="flex items-center gap-3 text-gray-800 font-bold"><i class="fa-solid fa-circle-check text-orange-500"></i> Gestion millimétrée des stocks de bouteilles</li>
                        </ul>
                        <router-link to="/cerfa" class="inline-flex items-center gap-2 text-white bg-gray-900 px-8 py-4 rounded-xl font-bold hover:bg-orange-600 transition-colors">
                            Voir la conformité F-Gaz <i class="fa-solid fa-arrow-right"></i>
                        </router-link>
                    </div>
                    <div class="lg:w-1/2 relative">
                        <div class="absolute inset-0 bg-gradient-to-l from-blue-400 to-teal-400 blur-3xl opacity-10 rounded-full"></div>
                        <img src="https://storage.termoflow.fr/file/RIAix46xch" alt="Mockup génération CERFA F-Gaz" class="relative z-10 w-full rounded-2xl shadow-2xl border border-gray-100 transform hover:-translate-y-2 transition-transform duration-500">
                    </div>
                </div>
            </div>
        </section>

        <section class="py-32 bg-gray-900 text-white overflow-hidden relative">
            <div class="absolute top-0 right-0 w-[800px] h-[800px] bg-orange-600/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3"></div>
            
            <div class="max-w-7xl mx-auto px-4 relative z-10">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                    <div class="order-2 lg:order-1 relative flex justify-center">
                        <img src="https://storage.victoranw.fr/file/p22bKkyuP8" alt="Application Mobile Technicien" class="w-80 relative z-10 drop-shadow-[0_30px_50px_rgba(0,0,0,0.7)] animate-float">
                    </div>
                    
                    <div class="order-1 lg:order-2">
                        <span class="inline-block py-1 px-3 rounded-full bg-orange-500/20 text-orange-400 font-bold text-xs uppercase tracking-widest mb-6 border border-orange-500/30">L'outil du technicien</span>
                        <h2 class="text-4xl md:text-5xl font-black mb-8 leading-tight">Une application mobile <br>qui marche <span class="text-orange-500">vraiment partout.</span></h2>
                        
                        <div class="space-y-8">
                            <div class="flex gap-6">
                                <div class="w-14 h-14 rounded-2xl bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0 text-orange-500 text-2xl shadow-inner"><i class="fa-solid fa-signal"></i></div>
                                <div>
                                    <h4 class="text-xl font-bold mb-2">100% Hors-ligne</h4>
                                    <p class="text-gray-400 leading-relaxed">Parce qu'il n'y a jamais de réseau dans une chaufferie. Saisissez rapports et CERFA sans coupure, la synchronisation se fera automatiquement.</p>
                                </div>
                            </div>
                            
                            <div class="flex gap-6">
                                <div class="w-14 h-14 rounded-2xl bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0 text-orange-500 text-2xl shadow-inner"><i class="fa-solid fa-camera"></i></div>
                                <div>
                                    <h4 class="text-xl font-bold mb-2">Photos & Documents</h4>
                                    <p class="text-gray-400 leading-relaxed">Prenez des photos avant/après. Créez instantanément des contrats d'entretien, attestation d'entretien, procès verbal de réception, CERFA, etc..</p>
                                </div>
                            </div>
                            
                            <div class="flex gap-6">
                                <div class="w-14 h-14 rounded-2xl bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0 text-orange-500 text-2xl shadow-inner"><i class="fa-solid fa-signature"></i></div>
                                <div>
                                    <h4 class="text-xl font-bold mb-2">Signature électronique</h4>
                                    <p class="text-gray-400 leading-relaxed">Faites signer vos documents sur smartphone ou tablette. Les PDF seront envoyés par email instantanément. Vous pouvez également envoyer un lien pour faire signer votre client post-intervention.</p>
                                </div>
                            </div>
                        </div>
                        
                        <div class="mt-12">
                            <router-link to="/application-mobile" class="inline-flex items-center gap-2 text-orange-500 font-bold text-lg hover:text-white transition-colors group">
                                Explorer l'application mobile <i class="fa-solid fa-arrow-right group-hover:translate-x-2 transition-transform"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-24 bg-gray-50 border-t border-gray-200">
            <div class="max-w-7xl mx-auto px-4">
                <div class="text-center mb-20">
                    <h2 class="text-4xl font-black text-gray-900 mb-6">
                        Structurez votre croissance & <span class="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">votre matériel</span>
                    </h2>
                    <p class="text-xl text-gray-600 max-w-3xl mx-auto">
                        Termoflow connecte le terrain au bureau. Fidélisez vos clients, analysez votre rentabilité avec précision et gardez un contrôle absolu sur vos équipements F-Gaz.
                    </p>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <router-link to="/contrats-entretien" class="bg-white p-10 rounded-[2rem] border border-gray-100 hover:border-orange-300 hover:shadow-xl transition-all group overflow-hidden relative">
                        <div class="relative z-10">
                            <div class="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center text-3xl text-orange-600 mb-8 group-hover:scale-110 transition-transform"><i class="fa-solid fa-handshake"></i></div>
                            <h3 class="text-2xl font-black mb-4">Contrats d'entretien & CRM</h3>
                            <p class="text-gray-600 leading-relaxed mb-6">
                                Gérez vos parcs de machines. L'outil relance automatiquement vos clients pour les maintenances annuelles et gère le portail d'accès aux documents.
                            </p>
                            <span class="text-orange-600 font-bold flex items-center gap-2">Découvrir la gestion client <i class="fa-solid fa-arrow-right text-sm"></i></span>
                        </div>
                        <img src="https://storage.victoranw.fr/file/YUQN6aQN7Y" alt="Mockup gestion client" class="absolute -right-20 -bottom-20 w-80 opacity-20 group-hover:opacity-100 transition-opacity duration-500 mix-blend-multiply">
                    </router-link>
                    
                    <router-link to="/statistiques" class="bg-white p-10 rounded-[2rem] border border-gray-100 hover:border-orange-300 hover:shadow-xl transition-all group overflow-hidden relative">
                        <div class="relative z-10">
                            <div class="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center text-3xl text-orange-600 mb-8 group-hover:scale-110 transition-transform"><i class="fa-solid fa-chart-pie"></i></div>
                            <h3 class="text-2xl font-black mb-4">Analyse de la rentabilité</h3>
                            <p class="text-gray-600 leading-relaxed mb-6">
                                Fini les intuitions. Pilotez avec des KPI clairs : rentabilité par contrat, marge par technicien, et export comptable en un clic.
                            </p>
                            <span class="text-orange-600 font-bold flex items-center gap-2">Voir les statistiques <i class="fa-solid fa-arrow-right text-sm"></i></span>
                        </div>
                        <img src="https://storage.victoranw.fr/file/y2qJIvRTUf" alt="Mockup statistiques entreprise" class="absolute -right-10 -bottom-24 w-80 opacity-20 group-hover:opacity-100 transition-opacity duration-500 mix-blend-multiply">
                    </router-link>

                    <router-link to="/bouteilles" class="bg-white p-10 rounded-[2rem] border border-gray-100 hover:border-orange-300 hover:shadow-xl transition-all group overflow-hidden relative">
                        <div class="relative z-10">
                            <div class="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center text-3xl text-orange-600 mb-8 group-hover:scale-110 transition-transform"><i class="fa-solid fa-fire"></i></div>
                            <h3 class="text-2xl font-black mb-4">Suivi des bouteilles de fluides</h3>
                            <p class="text-gray-600 leading-relaxed mb-6">
                                Traçabilité chirurgicale : bouteilles de charge ou récupération, type de gaz, charge actuelle et technicien assigné. Chaque mouvement de gaz est lié au CERFA correspondant et au numéro <strong>Trackdéchets</strong>.
                            </p>
                            <span class="text-orange-600 font-bold flex items-center gap-2">Gérer vos bouteilles <i class="fa-solid fa-arrow-right text-sm"></i></span>
                        </div>
                        <i class="fa-solid fa-flask absolute -right-6 -bottom-6 text-[10rem] text-gray-50 group-hover:text-orange-50 transition-colors z-0 transform -rotate-12"></i>
                    </router-link>

                    <router-link to="/detecteurs" class="bg-white p-10 rounded-[2rem] border border-gray-100 hover:border-orange-300 hover:shadow-xl transition-all group overflow-hidden relative">
                        <div class="relative z-10">
                            <div class="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center text-3xl text-orange-600 mb-8 group-hover:scale-110 transition-transform"><i class="fa-solid fa-pager"></i></div>
                            <h3 class="text-2xl font-black mb-4">Étalonnage des détecteurs</h3>
                            <p class="text-gray-600 leading-relaxed mb-6">
                                Ne laissez plus votre conformité au hasard. Sachez exactement quel détecteur de fuite est assigné à quel technicien et recevez des <strong>rappels automatiques par email</strong> avant l'échéance d'étalonnage.
                            </p>
                            <span class="text-orange-600 font-bold flex items-center gap-2">Suivi des équipements <i class="fa-solid fa-arrow-right text-sm"></i></span>
                        </div>
                        <i class="fa-solid fa-tower-broadcast absolute -right-6 -bottom-6 text-[10rem] text-gray-50 group-hover:text-orange-50 transition-colors z-0 transform rotate-12"></i>
                    </router-link>
                </div>
            </div>
        </section>

        <landing-avis />

        <section class="py-16 bg-white border-t border-gray-100">
            <div class="max-w-4xl mx-auto px-4">

            <h2 class="text-3xl font-black mb-6">
            Logiciel de gestion pour chauffagistes et climatisation
            </h2>

            <p class="text-gray-600 leading-relaxed mb-4">
            Termoflow est un logiciel conçu pour les entreprises de chauffage, climatisation et maintenance CVC. 
            Il permet de gérer les interventions, les contrats d'entretien, les stocks de fluides frigorigènes 
            et la conformité F-Gaz depuis une seule plateforme.
            </p>

            <p class="text-gray-600 leading-relaxed">
            Grâce à son application mobile hors-ligne, les techniciens peuvent remplir leurs rapports, 
            générer les CERFA et faire signer les clients directement sur le terrain. 
            Le bureau garde une vision claire du planning, de la rentabilité des interventions 
            et du suivi des équipements.
            </p>

            </div>
        </section>

        <section class="py-32 relative overflow-hidden">
            <div class="absolute inset-0 bg-gray-900"></div>
            <div class="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay"></div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-full bg-gradient-to-r from-orange-600/30 to-red-600/30 blur-[100px] rounded-full"></div>
            
            <div class="max-w-7xl mx-auto px-4 relative z-10 text-center">
                <h2 class="text-5xl md:text-7xl font-black text-white mb-8">
                    Prêt à passer à <br>
                    <span class="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">la vitesse supérieure ?</span>
                </h2>
                <p class="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
                    Rejoignez des centaines d'entreprises CVC qui ont déjà digitalisé et simplifié leur activité avec Termoflow.
                </p>
                
                <div class="flex flex-col sm:flex-row justify-center gap-6">
                    <router-link to="/register" class="bg-orange-600 text-white px-12 py-6 rounded-2xl font-black text-2xl shadow-2xl shadow-orange-600/40 hover:bg-orange-500 hover:scale-105 transition-all duration-300">
                        Démarrer l'essai gratuit
                    </router-link>
                    <router-link to="/contact" class="bg-transparent border-2 border-gray-600 text-white px-12 py-6 rounded-2xl font-black text-2xl hover:bg-white hover:text-gray-900 transition-all duration-300">
                        Parler à un expert
                    </router-link>
                </div>
                
                <div class="mt-12 flex justify-center gap-8 text-gray-400 font-bold uppercase text-xs tracking-[0.2em]">
                    <span class="flex items-center gap-2"><i class="fa-solid fa-check text-orange-500"></i> Sans engagement</span>
                    <span class="flex items-center gap-2"><i class="fa-solid fa-check text-orange-500"></i> Setup en 24h</span>
                    <span class="flex items-center gap-2"><i class="fa-solid fa-check text-orange-500"></i> Support réactif</span>
                </div>
            </div>
        </section>

        <landing-footer />

        <div v-if="videoOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <div class="absolute inset-0 bg-gray-900/95 backdrop-blur-sm transition-opacity duration-500" @click="closeVideo" :class="videoAnimating ? 'opacity-100' : 'opacity-0'"></div>
            
            <div class="relative w-full max-w-6xl aspect-video rounded-3xl overflow-hidden shadow-2xl transition-all duration-500 ease-out bg-black" :class="videoAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'">
                <video ref="mainVideo" class="w-full h-full" controls playsinline>
                    <source src="https://storage.termoflow.fr/file/x6pvVSndVQ" type="video/mp4" />
                </video>
                
                <button @click="closeVideo" class="absolute -top-12 right-0 text-white opacity-70 hover:opacity-100 hover:text-orange-500 text-4xl transition-all z-50 lg:top-6 lg:right-6 lg:bg-black/50 lg:w-12 lg:h-12 lg:rounded-full lg:flex lg:items-center lg:justify-center">
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </div>
        </div>
    </main>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'

export default {
    name: 'TermoflowHome',
    components: { LandingHeader, LandingAvis, LandingFooter },
    data() {
        return {
            videoOpen: false,
            videoAnimating: false
        }
    },
    methods: {
        openVideo() {
            this.videoOpen = true
            this.$nextTick(() => {
                // Petit délai pour laisser le rendu DOM s'effectuer
                setTimeout(() => {
                    this.videoAnimating = true
                    if (this.$refs.mainVideo) this.$refs.mainVideo.play()
                }, 50)
            })
        },
        closeVideo() {
            this.videoAnimating = false
            setTimeout(() => {
                if (this.$refs.mainVideo) {
                    this.$refs.mainVideo.pause()
                    this.$refs.mainVideo.currentTime = 0
                }
                this.videoOpen = false
            }, 500) // Attendre la fin de l'animation CSS
        }
    }
}
</script>

<style scoped>
.animate-bounce-slow {
    animation: bounceSlow 3s infinite ease-in-out;
}

@keyframes bounceSlow {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
}

.animate-float {
    animation: float 6s ease-in-out infinite;
}

@keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
    100% { transform: translateY(0px); }
}
</style>